import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../services/api";
import { useAuthStore } from "../stores/authStore";
import { useFamilyStore } from "../stores/familyStore";
import ParentSidebar from "../components/ParentSidebar";
import BottomNav from "../components/BottomNav";
import { useTheme } from "../hooks/useTheme";
import type { Family, Kid, Completion, Cashout, TaskAssignment, Redemption } from "@starquest/core";
import styles from "./ParentHome.module.css";

export default function ParentHome() {
  const navigate = useNavigate();
  const { familyId, canApprove } = useAuthStore();
  const { kids, completions, cashouts, setFamily, setKids, setCompletions, setCashouts } = useFamilyStore();
  const theme = useTheme();

  const [loading,          setLoading]          = useState(true);
  const [rejectTarget,     setRejectTarget]     = useState<string | null>(null);
  const [rejectReason,     setRejectReason]     = useState("");
  const [rejecting,        setRejecting]        = useState(false);
  const [taskSubmissions,  setTaskSubmissions]  = useState<TaskAssignment[]>([]);
  const [pendingRedemptions, setPendingRedemptions] = useState<Redemption[]>([]);

  useEffect(() => {
    if (!familyId) return;
    setLoading(true);
    Promise.all([
      api.getFamily(familyId).then((f) => setFamily(f as Family)),
      api.listKids(familyId).then((k) => setKids(k as Kid[])),
      api.listCompletions(familyId, "pending").then((c) => setCompletions(c as Completion[])),
      api.listCashouts(familyId).then((c) => setCashouts(c as Cashout[])),
      api.listTaskAssignments(familyId, { status: "submitted" }).then((a) => setTaskSubmissions(a as TaskAssignment[])),
      api.listRedemptions(familyId).then((r) => setPendingRedemptions((r as Redemption[]).filter(x => x.status === "pending"))),
    ]).finally(() => setLoading(false));
  }, [familyId]);

  const pendingCashouts = cashouts.filter((c) => c.status === "pending");
  const pendingThemeRequests = kids.filter((k) => k.themeRequest).length;
  const totalPending = completions.length + taskSubmissions.length + pendingCashouts.length + pendingRedemptions.length + pendingThemeRequests;

  async function approveTheme(kidId: string) {
    if (!familyId) return;
    await api.approveTheme(familyId, kidId);
    api.listKids(familyId).then((k) => setKids(k as Kid[]));
  }

  async function approveTask(aid: string) {
    if (!familyId) return;
    await api.reviewTaskAssignment(familyId, aid, "approve");
    api.listTaskAssignments(familyId, { status: "submitted" }).then((a) => setTaskSubmissions(a as TaskAssignment[]));
    api.listKids(familyId).then((k) => setKids(k as Kid[]));
  }

  async function rejectTask(aid: string) {
    if (!familyId) return;
    await api.reviewTaskAssignment(familyId, aid, "reject");
    api.listTaskAssignments(familyId, { status: "submitted" }).then((a) => setTaskSubmissions(a as TaskAssignment[]));
  }

  async function approve(subId: string) {
    if (!familyId) return;
    await api.reviewCompletion(familyId, subId, "approve");
    api.listCompletions(familyId, "pending").then((c) => setCompletions(c as Completion[]));
    api.listKids(familyId).then((k) => setKids(k as Kid[]));
  }

  async function confirmReject() {
    if (!familyId || !rejectTarget) return;
    setRejecting(true);
    await api.reviewCompletion(familyId, rejectTarget, "reject", rejectReason.trim() || undefined);
    setRejectTarget(null); setRejectReason(""); setRejecting(false);
    api.listCompletions(familyId, "pending").then((c) => setCompletions(c as Completion[]));
  }

  return (
    <div className={styles.page}>
      <ParentSidebar pendingCount={completions.length} />

      <main className={styles.main}>
        <div className={styles.head}>
          <div>
            <h1 className={styles.h1}>Dashboard</h1>
            <div className={styles.sub}>
              {totalPending > 0
                ? `${totalPending} item${totalPending > 1 ? "s" : ""} need your attention`
                : "All caught up — nothing pending"}
            </div>
          </div>
          <div className={styles.actions}>
            <button className={`${styles.btn} ${styles.ghost}`} onClick={() => navigate("/parent/family-hub")}>+ Add {theme.kidRole}</button>
            <button className={`${styles.btn} ${styles.primary}`} onClick={() => navigate("/parent/add-challenge")}>+ Create {theme.challengeName}</button>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign:"center", padding:"40px 0", color:"rgba(240,240,255,0.4)", fontFamily:"var(--font-body)" }}>
            Loading dashboard…
          </div>
        ) : (
          <>
            {/* Stats row */}
            <div className={styles.stats}>
              <div className={styles.stat}>
                <div className={styles.statLabel}>{(theme.kidRole + "s").toUpperCase()}</div>
                <div className={styles.statVal}>{kids.length}</div>
              </div>
              <div className={styles.stat}>
                <div className={styles.statLabel}>Needs Attention</div>
                <div className={`${styles.statVal} ${totalPending > 0 ? styles.urgent : ""}`}>
                  {totalPending > 0 && <span aria-hidden>⚠ </span>}{totalPending}
                </div>
              </div>
              <div className={styles.stat}>
                <div className={styles.statLabel}>Pending Cashouts</div>
                <div className={`${styles.statVal} ${pendingCashouts.length > 0 ? styles.urgent : ""}`}>
                  {pendingCashouts.length > 0 && <span aria-hidden>⚠ </span>}{pendingCashouts.length}</div>
              </div>
              <div className={styles.stat}>
                <div className={styles.statLabel}>Total {theme.pointNamePlural} Earned</div>
                <div className={styles.statVal}>
                  {kids.reduce((sum, k) => sum + (k.pointBalance || 0), 0)}
                </div>
              </div>
            </div>

            <div className={styles.grid}>
              {/* Verification queue */}
              <div className={styles.card}>
                <h3 className={styles.cardTitle}>
                  {theme.challengeName} Verifications
                  <span className={styles.link} onClick={() => api.listCompletions(familyId!, "pending").then((c) => setCompletions(c as Completion[]))}>
                    Refresh
                  </span>
                </h3>
                {completions.length === 0 && <p className={styles.empty}>No pending {theme.challengeNamePlural.toLowerCase()} 🎉</p>}
                {completions.map((comp) => (
                  <div key={comp.completionId} className={styles.verifRow}>
                    <div className={styles.verifAva}>{theme.pointEmoji}</div>
                    <div className={styles.verifInfo}>
                      <div className={styles.verifName}>{comp.challengeId}</div>
                      <div className={styles.verifMeta}>
                        {kids.find((k) => k.kidId === comp.kidId)?.name ?? comp.kidId} ·{" "}
                        {new Date(comp.submittedAt).toLocaleTimeString()}
                      </div>
                    </div>
                    {comp.photoKey && (
                      <div className={styles.verifThumb} title="Has photo">📸</div>
                    )}
                    <div className={styles.verifBtns}>
                      <button className={`${styles.vbtn} ${styles.ok}`} onClick={() => approve(comp.completionId)}>✓ Approve</button>
                      <button className={`${styles.vbtn} ${styles.no}`} onClick={() => { setRejectTarget(comp.completionId); setRejectReason(""); }}>✗</button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Family overview */}
              <div className={styles.card}>
                <h3 className={styles.cardTitle}>{theme.kidRole} Status</h3>
                {kids.length === 0 && <p className={styles.empty}>No {(theme.kidRole + "s").toLowerCase()} yet — add one in Setup</p>}
                {kids.map((kid) => (
                  <div key={kid.kidId} className={styles.kidRow}>
                    <div className={styles.kidAvi}>{kid.avatar}</div>
                    <div className={styles.kidInfo}>
                      <div className={styles.kidName}>{kid.name}</div>
                      <div className={styles.kidMeta}>Lv.{kid.level} · {kid.streakDays}🔥 streak</div>
                    </div>
                    <div className={styles.kidStars}>{theme.pointEmoji} {kid.pointBalance}</div>
                  </div>
                ))}
              </div>
            </div>
            {/* Notification feed */}
            {totalPending > 0 && (
              <div style={{ marginTop: 20 }}>
                <h3 className={styles.cardTitle} style={{ marginBottom: 12, fontSize: 16 }}>
                  🔔 Needs Attention
                  <span style={{ fontSize: 11, color: "rgba(240,240,255,0.4)", fontWeight: 400 }}>
                    {totalPending} item{totalPending > 1 ? "s" : ""}
                  </span>
                </h3>

                {/* Pending task submissions */}
                {taskSubmissions.map((a) => (
                  <div key={a.assignmentId} className={styles.verifRow}>
                    <div className={styles.verifAva} style={{ fontSize: 20 }}>{a.taskEmoji}</div>
                    <div className={styles.verifInfo}>
                      <div className={styles.verifName}>
                        {kids.find((k) => k.kidId === a.kidId)?.name ?? "Explorer"} completed "{a.taskName}"
                      </div>
                      <div className={styles.verifMeta}>
                        Task · {a.tier === "early" ? "🌟 Early bird +20%" : a.tier === "late" ? "⏱️ Late -50%" : "✅ On time"} · {a.submittedAt ? new Date(a.submittedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                      </div>
                    </div>
                    {canApprove && <div className={styles.verifBtns}>
                      <button className={`${styles.vbtn} ${styles.ok}`} onClick={() => approveTask(a.assignmentId)}>✓ Approve</button>
                      <button className={`${styles.vbtn} ${styles.no}`} onClick={() => rejectTask(a.assignmentId)}>✗</button>
                    </div>}
                  </div>
                ))}

                {/* Pending quest completions */}
                {completions.map((comp) => (
                  <div key={comp.completionId} className={styles.verifRow}>
                    <div className={styles.verifAva}>{theme.pointEmoji}</div>
                    <div className={styles.verifInfo}>
                      <div className={styles.verifName}>
                        {kids.find((k) => k.kidId === comp.kidId)?.name ?? theme.kidRole} completed a {theme.challengeName.toLowerCase()}
                      </div>
                      <div className={styles.verifMeta}>{theme.challengeName} · {new Date(comp.submittedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                    </div>
                    {comp.photoKey && <div className={styles.verifThumb} title="Has photo">📸</div>}
                    {canApprove && <div className={styles.verifBtns}>
                      <button className={`${styles.vbtn} ${styles.ok}`} onClick={() => approve(comp.completionId)}>✓ Approve</button>
                      <button className={`${styles.vbtn} ${styles.no}`} onClick={() => { setRejectTarget(comp.completionId); setRejectReason(""); }}>✗</button>
                    </div>}
                  </div>
                ))}

                {/* Pending cashout requests */}
                {pendingCashouts.map((c) => (
                  <div key={c.cashoutId} className={styles.verifRow}>
                    <div className={styles.verifAva} style={{ fontSize: 20 }}>🏦</div>
                    <div className={styles.verifInfo}>
                      <div className={styles.verifName}>
                        {kids.find((k) => k.kidId === c.kidId)?.name ?? "Explorer"} requested a cashout
                      </div>
                      <div className={styles.verifMeta}>{c.points} {theme.pointEmoji} → ${c.dollars.toFixed(2)}</div>
                    </div>
                    <div className={styles.verifBtns}>
                      <button className={`${styles.vbtn} ${styles.no}`} onClick={() => navigate("/parent/points-value")}>Review →</button>
                    </div>
                  </div>
                ))}

                {/* Pending redemption requests */}
                {pendingRedemptions.map((r) => (
                  <div key={r.redemptionId} className={styles.verifRow}>
                    <div className={styles.verifAva} style={{ fontSize: 20 }}>{r.itemEmoji}</div>
                    <div className={styles.verifInfo}>
                      <div className={styles.verifName}>
                        {kids.find((k) => k.kidId === r.kidId)?.name ?? "Explorer"} wants to redeem "{r.itemName}"
                      </div>
                      <div className={styles.verifMeta}>{r.pointCost} {theme.pointEmoji} · Store</div>
                    </div>
                    <div className={styles.verifBtns}>
                      <button className={`${styles.vbtn} ${styles.no}`} onClick={() => navigate("/parent/store")}>Review →</button>
                    </div>
                  </div>
                ))}

                {/* Theme requests */}
                {kids.filter((k) => k.themeRequest).map((k) => {
                  const themeId = k.themeRequest!.themeId;
                  const themeLabel: Record<string, string> = { explorer: "Explorer 🗺️", wizard: "Wizard 🧙", sports: "Sports 🏆" };
                  return (
                    <div key={`theme-${k.kidId}`} className={styles.verifRow}>
                      <div className={styles.verifAva} style={{ background: "linear-gradient(135deg,#a855f7,#7c3aed)" }}>🎨</div>
                      <div className={styles.verifInfo}>
                        <div className={styles.verifName}>{k.name} wants a new theme</div>
                        <div className={styles.verifMeta}>{themeLabel[themeId] ?? themeId}</div>
                      </div>
                      <div className={styles.verifBtns}>
                        <button className={`${styles.vbtn} ${styles.ok}`} onClick={() => approveTheme(k.kidId)}>✓ Approve</button>
                      </div>
                    </div>
                  );
                })}

                {/* Auto-applied notifications — shown for 7 days */}
                {kids.filter((k) => {
                  if (!k.themeAutoAppliedAt) return false;
                  return Date.now() - new Date(k.themeAutoAppliedAt).getTime() < 7 * 24 * 60 * 60 * 1000;
                }).map((k) => {
                  const themeLabel: Record<string, string> = { explorer: "Explorer 🗺️", wizard: "Wizard 🧙", sports: "Sports 🏆" };
                  return (
                    <div key={`auto-${k.kidId}`} className={styles.verifRow}>
                      <div className={styles.verifAva} style={{ background: "linear-gradient(135deg,#4ade80,#16a34a)" }}>✅</div>
                      <div className={styles.verifInfo}>
                        <div className={styles.verifName}>{k.name}'s theme was auto-applied</div>
                        <div className={styles.verifMeta}>{themeLabel[k.theme ?? ""] ?? k.theme} · applied after 7 days</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
        {rejectTarget && (
          <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:100 }}
            onClick={(e) => e.target === e.currentTarget && setRejectTarget(null)}>
            <div style={{ background:"#1a1a35",border:"1px solid rgba(255,255,255,0.1)",borderRadius:20,padding:28,maxWidth:440,width:"90%",display:"flex",flexDirection:"column",gap:16 }}>
              <div style={{ fontFamily:"var(--font-display)",fontWeight:900,fontSize:18 }}>Reject this quest?</div>
              <div style={{ color:"rgba(240,240,255,0.55)",fontSize:14 }}>Let your explorer know why so they can try again.</div>
              <textarea
                style={{ background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:8,padding:"10px 14px",color:"var(--star)",fontFamily:"inherit",fontSize:14,resize:"vertical",outline:"none" }}
                placeholder="e.g. The photo wasn't clear enough. Try again!"
                value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                rows={3} autoFocus
              />
              <div style={{ display:"flex",gap:10 }}>
                <button className={`${styles.vbtn} ${styles.no}`} onClick={confirmReject} disabled={rejecting} style={{ padding:"10px 20px",fontSize:13 }}>
                  {rejecting ? "Rejecting…" : "✗ Reject Quest"}
                </button>
                <button className={`${styles.btn} ${styles.ghost}`} onClick={() => setRejectTarget(null)}>Cancel</button>
              </div>
            </div>
          </div>
        )}
      </main>
      <BottomNav pendingCount={totalPending} />
    </div>
  );
}
